import React, { useState, useEffect, lazy, Suspense } from 'react';
import { motion, AnimatePresence } from 'motion/react';
import { Download, ArrowRight, Building2, Shield, PieChart, Search, Loader2, MapPin, HelpCircle, ClipboardCheck, FileText, GraduationCap, Settings, Headphones, Mic } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import GlassCard from '../components/GlassCard';
import HSDashboard from '../components/HSDashboard';
const CloudRunDashboard = lazy(() => import('../components/CloudRunDashboard'));
import { CloudRunFractionResult } from '../components/CloudRunDashboard';
import VideoCarousel from '../components/VideoCarousel';
import { useLanguage } from '../context/LanguageContext';
import { searchFraccionesDb } from '../lib/hsDbSearch';
import { useTheme } from '../context/ThemeContext';

const mapHsFractionToCloudRun = (item: any): CloudRunFractionResult => {
  const taxesStr = item.taxes ? [
    item.taxes.igi ? `IGI: ${item.taxes.igi}` : '',
    item.taxes.iva ? `IVA: ${item.taxes.iva}` : '',
    item.taxes.dta ? `DTA: ${item.taxes.dta}` : '',
    item.taxes.ieps ? `IEPS: ${item.taxes.ieps}` : '',
    item.taxes.treaty ? `Tratado: ${item.taxes.treaty}` : ''
  ].filter(Boolean).join('\n') : '';

  const regsStr = Array.isArray(item.regulations) ? item.regulations.join('\n') : '';
  const docsStr = Array.isArray(item.documents) ? item.documents.join('\n') : '';
  const alertsStr = Array.isArray(item.aiAlerts) ? item.aiAlerts.join('\n') : '';

  // Construct a comprehensive respuestaIA markdown-friendly text replicating the structured response
  const dictamenParts = [];
  if (item.smartAnalysis) {
    dictamenParts.push(`**DICTAMEN Y ANÁLISIS DE LA FRACCIÓN:**\n${item.smartAnalysis}`);
  }
  if (taxesStr) {
    dictamenParts.push(`**ESTRUCTURA ARANCELARIA APLICABLE:**\n${taxesStr}`);
  }
  if (regsStr) {
    dictamenParts.push(`**REGULACIONES Y RESTRICCIONES NO ARANCELARIAS (RRNA):**\n${regsStr}`);
  }
  if (docsStr) {
    dictamenParts.push(`**DOCUMENTACIÓN REQUERIDA PARA EL DESPACHO ADUANERO:**\n${docsStr}`);
  }
  if (alertsStr) {
    dictamenParts.push(`**ALERTAS CRÍTICAS DE IMPORTACIÓN / CONTINGENCIAS DE ADUANA:**\n${alertsStr}`);
  }

  return {
    fraction: item.hsCode,
    fraction_id: item.hsCode,
    description: item.description,
    duties: item.taxes ? `${item.taxes.igi || 'Exento'} IGI, ${item.taxes.iva || '16%'} IVA` : 'Consultar tasa',
    hierarchy: 'Sección LIGIE / Base de Datos Complementaria',
    nico: item.hsCode && item.hsCode.includes('.') ? item.hsCode.split('.').pop() : '00',
    restrictions: regsStr || 'Consulte regulaciones y padrones de sectores específicos en el panel',
    observations: item.status || 'Activo y vigente en el catálogo de aduanas',
    respuestaIA: dictamenParts.join('\n\n')
  };
};

export default function Home() {
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const location = useLocation();

  // Ticker State
  const [stats, setStats] = useState([
    { name: t('home.stats.mxn'), value: '18.42', trend: '+0.12%', up: true },
    { name: t('home.stats.freight'), value: '$3,824', trend: '-1.50%', up: false },
    { name: t('home.stats.shanghai'), value: '2,145', trend: '+0.80%', up: true },
    { name: t('home.stats.demand'), value: t('home.stats.optimal'), trend: '', up: true },
  ]);

  // HS Search State (Home)
  const [hsQuery, setHsQuery] = useState('');
  const [hsResult, setHsResult] = useState<any>(null);
  const [loadingHs, setLoadingHs] = useState(false);
  const [multipleMatches, setMultipleMatches] = useState<any[]>([]);
  const [searchNoMatch, setSearchNoMatch] = useState<boolean>(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [cloudRunResult, setCloudRunResult] = useState<CloudRunFractionResult | null>(null);
  const [cloudRunResultsList, setCloudRunResultsList] = useState<CloudRunFractionResult[]>([]);

  useEffect(() => {
    async function fetchStats() {
      try {
        const response = await fetch('https://open.er-api.com/v6/latest/USD');
        if (!response.ok) throw new Error("API not ok");
        const data = await response.json();
        const mxnRate = data.rates.MXN;

        setStats(prev => [
          { ...prev[0], value: mxnRate.toFixed(2) },
          { ...prev[1], value: '$' + (3820 + Math.random() * 50).toFixed(0) },
          { ...prev[2], value: (2140 + Math.random() * 10).toFixed(1) },
          prev[3]
        ]);
      } catch (error) {
        console.error("Error fetching stats:", error);
        setStats(prev => [
          { ...prev[0], value: "17.15" },
          { ...prev[1], value: '$3,845' },
          { ...prev[2], value: "2145.2" },
          prev[3]
        ]);
      }
    }
    fetchStats();
    const interval = setInterval(fetchStats, 30000);
    return () => clearInterval(interval);
  }, [t]);

  const handleHsSearch = async (e?: React.FormEvent, directQuery?: string) => {
    if (e) e.preventDefault();
    const queryToUse = directQuery !== undefined ? directQuery : hsQuery;
    if (!queryToUse || !queryToUse.trim()) return;

    setLoadingHs(true);
    setCloudRunResult(null);
    setCloudRunResultsList([]);
    setHsResult(null);
    setMultipleMatches([]);
    setSearchNoMatch(false);
    setSearchError(null);

    // Immediate scroll to anchor so the user sees the loading container
    setTimeout(() => {
      const el = document.getElementById('ai-dashboard-anchor');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 100);

    let apiResultsList: CloudRunFractionResult[] = [];
    let localResultsList: CloudRunFractionResult[] = [];

    // 1. Query the local/Firestore database (covers prefixes, fallback catalog, scanning)
    const localPromise = (async () => {
      try {
        const localMatches = await searchFraccionesDb(queryToUse.trim());
        if (localMatches && localMatches.length > 0) {
          localResultsList = localMatches.map(mapHsFractionToCloudRun);
        }
      } catch (localErr) {
        console.warn("Local/Firestore hsDbSearch failed:", localErr);
      }
    })();

    // 2. Query the remote Cloud Run API proxy
    const apiPromise = (async () => {
      const trimmedQuery = queryToUse.trim();

      try {
        let responseData: any = null;
        const res = await fetch(`/api/search?q=${encodeURIComponent(trimmedQuery)}`);
        if (res.ok) {
          responseData = await res.json();
        }

        if (responseData) {
          if (responseData.resultados && Array.isArray(responseData.resultados)) {
            apiResultsList = responseData.resultados;
          } else if (Array.isArray(responseData)) {
            apiResultsList = responseData;
          } else if (typeof responseData === 'object' && responseData !== null) {
            apiResultsList = [responseData];
          }
        }
      } catch (apiErr) {
        console.warn("API lookup failed, fallback to local/firestore:", apiErr);
      }
    })();

    // Await both sources in parallel for fast response
    await Promise.all([localPromise, apiPromise]);

    try {
      const mergedList: CloudRunFractionResult[] = [];
      const seenFractions = new Set<string>();

      // Load API results first (highest specificity)
      for (const item of apiResultsList) {
        const code = (item.fraction || item.fraction_id || '').trim();
        if (code && !seenFractions.has(code)) {
          seenFractions.add(code);
          mergedList.push(item);
        }
      }

      // Load local catalog/Firestore prefix results if unique
      for (const item of localResultsList) {
        const code = (item.fraction || item.fraction_id || '').trim();
        if (code && !seenFractions.has(code)) {
          seenFractions.add(code);
          mergedList.push(item);
        }
      }

      if (mergedList.length > 0) {
        setCloudRunResult(mergedList[0]);
        setCloudRunResultsList(mergedList);
        setSearchError(null); // Ensure no error is shown if we have results
      } else {
        throw new Error("No se pudo obtener la información.");
      }

      const scrollToAnchor = () => {
        const el = document.getElementById('ai-dashboard-anchor');
        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      };

      setTimeout(scrollToAnchor, 150);
      setTimeout(scrollToAnchor, 400);
      setTimeout(scrollToAnchor, 700);

    } catch (err: any) {
      console.error("Combined lookup failed:", err);
      setSearchError("No se encontró información en la base de datos de aduanas. Favor de verificar que la clave sea válida (ej. 0101, 0804.40.01) o usar una descripción comercial.");
    } finally {
      setLoadingHs(false);
    }
  };

  // Listen to navigation state from other pages
  useEffect(() => {
    if (location.state && location.state.searchQuery) {
      const query = location.state.searchQuery;
      setHsQuery(query);
      handleHsSearch(undefined, query);
      
      // Clear location state to prevent repeating the search on other triggers
      navigate(location.pathname, { replace: true, state: {} });
    }
  }, [location, navigate]);

  const handleReportDownload = () => {
    // Implementation of report download
    const reportUrl = 'https://www.wto.org/english/res_e/booksp_e/world_trade_report23_e.pdf'; 
    window.open(reportUrl, '_blank');
  };

  const services = [
    {
      title: language === 'en' ? 'Advisory' : 'Asesoría',
      desc: language === 'en' 
        ? 'We resolve your doubts and present solutions to the specific cases that arise in your day-to-day operations.'
        : 'Resolvemos sus dudas y presentamos soluciones a los casos específicos que se le presenten día a día.',
      icon: HelpCircle,
      path: '/compliance'
    },
    {
      title: language === 'en' ? 'Auditing' : 'Auditoría',
      desc: language === 'en'
        ? 'We verify your foreign trade operations, preventing customs and fiscal contingencies.'
        : 'Verificamos sus operaciones de comercio exterior evitando contingencias aduanales y fiscales.',
      icon: ClipboardCheck,
      path: '/compliance'
    },
    {
      title: language === 'en' ? 'Procedures' : 'Tramitología',
      desc: language === 'en'
        ? 'We handle the necessary procedures for your operations before customs and trade-related authorities.'
        : 'Realizamos los trámites necesarios para su operación antes las autoridades aduaneras y relacionadas al comercio exterior.',
      icon: FileText,
      path: '/compliance'
    },
    {
      title: language === 'en' ? 'Training' : 'Capacitación',
      desc: language === 'en'
        ? 'We train your departments with the goal of correctly carrying out their foreign trade operations.'
        : 'Capacitamos a sus departamentos con la finalidad de que puedan llevar de manera correcta sus operaciones de comercio exterior.',
      icon: GraduationCap,
      path: '/events'
    },
    {
      title: language === 'en' ? 'Management' : 'Gestión',
      desc: language === 'en'
        ? 'We manage your foreign trade operations by outsourcing your processes with our expert team.'
        : 'Gestionamos sus operaciones de comercio exterior a través de la tercerización de sus procesos con nuestro equipo de expertos en comercio exterior.',
      icon: Settings,
      path: '/compliance'
    }
  ];

  return (
    <main className="pt-0">
      {/* Hero Section */}
      <section className="relative min-h-[85vh] flex flex-col justify-center pt-24 pb-16 px-6 md:px-12 overflow-hidden">
        {/* Background Visual */}
        <div className="absolute inset-0 z-0 pointer-events-none overflow-hidden">
          <img 
            src="https://images.unsplash.com/photo-1578575437130-527eed3abbec?q=80&w=1400" 
            alt="International Port Logistics Terminal" 
            width="1400"
            height="700"
            fetchPriority="high"
            className="w-full h-full object-cover opacity-75 grayscale-[10%] brightness-[85%]"
            referrerPolicy="no-referrer"
            onError={(e) => {
              e.currentTarget.onerror = null;
              e.currentTarget.src = 'https://images.unsplash.com/photo-1586528116311-ad8dd3c8310d?q=80&w=1400';
            }}
          />
          <div className="absolute inset-0 bg-zinc-950/40" />
          <div className="absolute inset-0 bg-gradient-to-r dark:from-background dark:via-background/75 from-background/20 via-background/5 to-transparent" />
          <div className="absolute bottom-0 left-0 w-full h-[25%] bg-gradient-to-t dark:from-background from-background/10 to-transparent" />
        </div>

        <div className="max-w-4xl space-y-8 relative z-10">
          <motion.div 
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            className="inline-flex items-center gap-3 px-4 py-1.5 bg-black/60 backdrop-blur-md rounded-full border border-primary/35 shadow-[0_0_15px_rgba(254,59,31,0.25)]"
          >
            <span className="w-2 h-2 rounded-full bg-primary animate-intense-glow"></span>
            <span className="label-caps text-[#d4d4d8] text-[11px] font-bold tracking-widest uppercase">Global Ecosystem V2.4</span>
          </motion.div>

          <motion.h1 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="font-hanken text-5xl md:text-7xl font-bold tracking-tighter leading-[1.05] text-on-surface"
          >
            {t('home.hero.titleL1')} <br />
            <span className="text-primary">{t('home.hero.titleL2')}</span>
          </motion.h1>

          <motion.p 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-[#f4f4f5] max-w-2xl font-medium"
          >
            {t('home.hero.description')}
          </motion.p>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="flex flex-wrap gap-6 pt-4"
          >
            <button 
              onClick={() => navigate('/consultation-booking')}
              className="primary-button text-lg"
            >
              {t('home.hero.cta1')}
            </button>
            <button 
              onClick={() => navigate('/events')}
              className="bg-transparent border border-[#FE3B1F] text-white font-bold uppercase tracking-wider backdrop-blur-lg hover:bg-[#FE3B1F] hover:shadow-[0_0_30px_rgba(254,59,31,0.75)] active:scale-98 transition-all duration-300 px-6 py-3 text-lg flex items-center justify-center rounded-sm group"
            >
              <div className="flex items-center gap-2">
                {t('home.hero.cta2')}
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </div>
            </button>
            <button 
              onClick={handleReportDownload}
              className="flex items-center gap-2 text-on-surface hover:text-primary transition-all label-caps underline underline-offset-8 decoration-primary/30 hover:decoration-primary"
            >
              {t('home.hero.report')}
              <Download size={18} className="animate-bounce" />
            </button>
          </motion.div>
        </div>
      </section>

      {/* Ticker Row */}
      <section className="border-y border-border-glass bg-surface-container-lowest/50 backdrop-blur-md overflow-hidden">
        <div className="flex items-center py-5 px-6 md:px-12 gap-16 overflow-x-auto no-scrollbar whitespace-nowrap">
          {stats.map((stat, i) => (
            <div key={i} className="flex items-center gap-12 group last:pr-12">
              <div className="flex items-center gap-4">
                <span className="label-caps text-on-surface-variant text-[10px] whitespace-nowrap font-bold">{stat.name}</span>
                <AnimatePresence mode="wait">
                  <motion.span 
                    key={stat.value}
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="font-hanken text-xl font-medium tracking-tight whitespace-nowrap text-on-surface"
                  >
                    {stat.value} 
                    {stat.trend && (
                      <span className={`text-[12px] opacity-90 ml-1 ${stat.up ? 'text-emerald-600 dark:text-emerald-400' : 'text-red-600 dark:text-red-400'}`}>
                        {stat.up ? '▲' : '▼'} {stat.trend}
                      </span>
                    )}
                  </motion.span>
                </AnimatePresence>
              </div>
              {i < stats.length - 1 && <div className="w-px h-6 bg-border-glass" />}
            </div>
          ))}
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 px-6 md:px-12">
        <div className="text-center max-w-xl mx-auto mb-16 space-y-2">
          <span className="label-caps text-primary">{t('home.solutions.label')}</span>
          <h2 className="font-hanken text-4xl font-bold tracking-tight">{t('home.solutions.title')}</h2>
        </div>
        <div className="flex flex-col gap-6 max-w-7xl mx-auto">
          {/* Top Row: 2 Cards Centered */}
          <div className="flex flex-wrap justify-center gap-6 w-full">
            {services.slice(0, 2).map((service, i) => {
              const IconComponent = service.icon;
              return (
                <GlassCard 
                  key={i} 
                  onClick={() => navigate(service.path)}
                  className="flex flex-col h-full group border border-border-glass hover:border-primary hover:shadow-2xl hover:shadow-primary/15 hover:-translate-y-1 transition-all duration-500 relative overflow-hidden w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] min-h-[320px] rounded-2xl cursor-pointer"
                >
                  {/* Intense visual ambient inner glow on hover */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/15 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                  
                  {/* Small Header Icon with pulsed/scale effects */}
                  <div className="w-12 h-12 bg-primary/15 flex items-center justify-center mb-8 border border-primary/25 rounded-sm relative z-10 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="text-primary w-6 h-6 animate-pulse" />
                  </div>
                  
                  {/* Text & Button content above z-index */}
                  <div className="relative z-10 flex flex-col flex-grow">
                    <h3 className="font-hanken text-2xl font-semibold mb-4 transition-colors duration-300">{service.title}</h3>
                    <p className="text-on-secondary-container mb-8 flex-grow leading-relaxed italic text-sm">
                      {service.desc}
                    </p>
                    <div className="label-caps text-on-surface group-hover:text-primary transition-colors flex items-center gap-2 mt-auto self-start font-semibold">
                      {t('home.solutions.explore')} 
                      <ArrowRight size={16} className="group-hover:translate-x-1.5 transition-transform" />
                    </div>
                  </div>

                  {/* Highly visible, attractive decorative watermarked background icon */}
                  <div className="absolute -bottom-8 -right-8 opacity-[0.12] dark:opacity-[0.08] text-primary pointer-events-none group-hover:scale-125 group-hover:-rotate-12 group-hover:opacity-[0.25] dark:group-hover:opacity-[0.18] transition-all duration-700 select-none z-0">
                    <IconComponent size={160} strokeWidth={0.75} />
                  </div>
                </GlassCard>
              );
            })}
          </div>

          {/* Bottom Row: 3 Cards Centered */}
          <div className="flex flex-wrap justify-center gap-6 w-full">
            {services.slice(2).map((service, i) => {
              const IconComponent = service.icon;
              return (
                <GlassCard 
                  key={i + 2} 
                  onClick={() => navigate(service.path)}
                  className="flex flex-col h-full group border border-border-glass hover:border-primary hover:shadow-2xl hover:shadow-primary/15 hover:-translate-y-1 transition-all duration-500 relative overflow-hidden w-full md:w-[calc(50%-12px)] lg:w-[calc(33.333%-16px)] min-h-[320px] rounded-2xl cursor-pointer"
                >
                  {/* Intense visual ambient inner glow on hover */}
                  <div className="absolute inset-0 bg-gradient-to-tr from-primary/15 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700 pointer-events-none" />
                  
                  {/* Small Header Icon with pulsed/scale effects */}
                  <div className="w-12 h-12 bg-primary/15 flex items-center justify-center mb-8 border border-primary/25 rounded-sm relative z-10 group-hover:scale-110 transition-transform duration-300">
                    <IconComponent className="text-primary w-6 h-6 animate-pulse" />
                  </div>
                  
                  {/* Text & Button content above z-index */}
                  <div className="relative z-10 flex flex-col flex-grow">
                    <h3 className="font-hanken text-2xl font-semibold mb-4 transition-colors duration-300">{service.title}</h3>
                    <p className="text-on-secondary-container mb-8 flex-grow leading-relaxed italic text-sm">
                      {service.desc}
                    </p>
                    <div className="label-caps text-on-surface group-hover:text-primary transition-colors flex items-center gap-2 mt-auto self-start font-semibold">
                      {t('home.solutions.explore')} 
                      <ArrowRight size={16} className="group-hover:translate-x-1.5 transition-transform" />
                    </div>
                  </div>

                  {/* Highly visible, attractive decorative watermarked background icon */}
                  <div className="absolute -bottom-8 -right-8 opacity-[0.12] dark:opacity-[0.08] text-primary pointer-events-none group-hover:scale-125 group-hover:-rotate-12 group-hover:opacity-[0.25] dark:group-hover:opacity-[0.18] transition-all duration-700 select-none z-0">
                    <IconComponent size={160} strokeWidth={0.75} />
                  </div>
                </GlassCard>
              );
            })}
          </div>
        </div>
      </section>

      <VideoCarousel />

      {/* Podcast Section */}
      <section className="py-20 px-6 md:px-12 bg-gradient-to-br from-[#FE3B1F] via-[#E22B12] to-[#991102] relative overflow-hidden border-t border-white/10">
        {/* Subtle geometric light pattern over red background */}
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,rgba(255,255,255,0.1),transparent_60%)] pointer-events-none" />
        
        <div className="max-w-7xl mx-auto w-full relative z-10">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            
            {/* Visual Promo (Left / Col-5) */}
            <div className="lg:col-span-5 flex justify-center">
              <div className="relative group w-full max-w-[340px]">
                {/* Outer soft glow ring (white/pink) */}
                <div className="absolute -inset-2 bg-white/20 rounded-3xl blur-xl opacity-60 group-hover:opacity-90 transition duration-500" />
                
                {/* Photographic Cover Container */}
                <div className="relative bg-zinc-950 rounded-3xl overflow-hidden aspect-square shadow-2xl border border-white/20">
                  <img
                    src="https://images.unsplash.com/photo-1590602847861-f357a9332bbc?q=80&w=600&auto=format&fit=crop"
                    alt="TREID Comercio Exterior Podcast Cover"
                    width="600"
                    height="600"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
                    referrerPolicy="no-referrer"
                    loading="lazy"
                  />
                  {/* Branding elements overlay on image */}
                  <div className="absolute inset-0 bg-gradient-to-t from-black/95 via-black/45 to-transparent flex flex-col justify-end p-6">
                    <span className="text-[10px] uppercase font-bold tracking-widest text-[#FE3B1F] bg-white px-3 py-1 rounded-full border border-white/10 w-fit self-start shadow-md mb-2">
                      Podcast Oficial
                    </span>
                    <h4 className="font-hanken text-2xl font-black text-white tracking-tight leading-none uppercase">
                      TREID
                    </h4>
                    <p className="text-[10px] text-zinc-300 font-mono uppercase tracking-widest mt-1">
                      Comercio Exterior
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Core copy & Links (Right / Col-7) */}
            <div className="lg:col-span-7 space-y-6 text-center lg:text-left flex flex-col justify-center">
              <div className="space-y-3">
                <span className="text-zinc-950 bg-white/90 text-xs px-3 py-1 font-mono rounded-full font-black tracking-widest inline-block select-none mb-1 shadow-sm uppercase">
                  {t('home.podcast.label')}
                </span>
                <h2 className="font-hanken text-4xl md:text-5xl font-black tracking-tighter leading-none text-white">
                  {t('home.podcast.title')}
                </h2>
                <p className="text-red-50 text-base md:text-lg font-light leading-relaxed max-w-2xl opacity-90">
                  {t('home.podcast.description')}
                </p>
              </div>

              {/* Grid / list of premium platform buttons */}
              <div className="flex flex-col sm:flex-row gap-4 pt-2 justify-center lg:justify-start">
                <a 
                  id="podcast-spotify-link"
                  href="https://open.spotify.com/show/4nOotwTaCS4AWA0jA9sxen?si=HSQfw1qpSnOgp1Cz5zhHSg&nd=1"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-zinc-950 hover:bg-[#1DB954] text-white hover:text-black font-extrabold px-6 py-4 rounded-2xl shadow-xl transition-all duration-300 hover:-translate-y-0.5 focus:ring-4 focus:ring-white/20 select-none group"
                >
                  <svg className="w-5 h-5 fill-current transition-transform group-hover:scale-110" viewBox="0 0 24 24">
                    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm4.586 14.424c-.18.295-.565.387-.86.207-2.377-1.454-5.37-1.783-8.893-.98-.336.075-.67-.14-.744-.477-.076-.336.14-.67.477-.745 3.856-.88 7.15-.494 9.813 1.136.295.18.387.563.207.86zm1.224-2.72c-.227.367-.707.487-1.074.26-2.72-1.672-6.87-2.157-10.076-1.183-.413.125-.845-.107-.97-.52-.125-.413.107-.845.52-.97 3.665-1.112 8.236-.575 11.34 1.332.367.226.487.707.26 1.073zm.106-2.833C14.384 8.78 8.448 8.583 5.018 9.625c-.527.16-1.08-.147-1.24-.674-.16-.527.147-1.081.674-1.24 3.948-1.198 10.512-.97 14.548 1.427.473.282.63.893.347 1.367-.282.473-.893.63-1.367.347z"/>
                  </svg>
                  <span>{t('home.podcast.listen_spotify')}</span>
                </a>

                <a 
                  id="podcast-apple-link"
                  href="https://podcasts.apple.com/mx/podcast/treid-comercio-exterior/id1643977751"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center gap-3 bg-zinc-950 hover:bg-[#D83BDE] text-white font-extrabold px-6 py-4 rounded-2xl shadow-xl transition-all duration-300 hover:-translate-y-0.5 focus:ring-4 focus:ring-white/20 select-none group"
                >
                  <svg className="w-5 h-5 fill-current transition-transform group-hover:scale-110" viewBox="0 0 24 24">
                    <path d="M12 2C6.477 2 2 6.477 2 12s4.477 10 10 10 10-4.477 10-10S17.523 2 12 2zm1.6 15.3v-2.1c.5-.3.8-.9.8-1.5 0-1-.8-1.8-1.8-1.8s-1.8.8-1.8 1.8c0 .6.3 1.2.8 1.5v2.1c-1.9-.3-3.4-1.9-3.4-3.9 0-2.2 1.8-4 4-4s4 1.8 4 4c0 2-1.5 3.6-3.4 3.9zm-1.6-9c-2.3 0-4.2 1.9-4.2 4.2 0 1.5.8 2.8 2 3.5-.2-.4-.3-.8-.3-1.3 0-1.4 1.1-2.5 2.5-2.5s2.5 1.1 2.5 2.5c0 .5-.1.9-.3 1.3 1.2-.7 2-2 2-3.5 0-2.3-1.9-4.2-4.2-4.2zm0-2.8C8.3 5.5 5.5 8.3 5.5 12c0 2.4 1.3 4.5 3.2 5.6-.2-.6-.3-1.3-.3-2 0-2.5 2-4.5 4.5-4.5s4.5 2 4.5 4.5c0 .7-.1 1.4-.3 2 1.9-1.1 3.2-3.2 3.2-5.6 0-3.7-2.8-6.5-6.5-6.5z"/>
                  </svg>
                  <span>{t('home.podcast.listen_apple')}</span>
                </a>
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* Intelligence Widget Preview */}
      <section className="py-24 px-6 md:px-12 bg-surface-container-lowest/30 backdrop-blur-md relative border-t border-border-glass">
        <div className="max-w-7xl mx-auto w-full flex flex-col items-center transition-all duration-500">
          <div className="w-full max-w-4xl mx-auto space-y-8 flex flex-col items-center text-center">
            <div className="space-y-4 flex flex-col items-center">
              <span className="label-caps text-primary text-center block">{t('home.intelligence.label')}</span>
              <h2 className="font-hanken text-[40px] md:text-5xl font-black tracking-tighter leading-tight text-center max-w-3xl">{t('home.intelligence.title')}</h2>
              <p className="text-on-secondary-container text-base md:text-lg font-light leading-relaxed max-w-2xl text-center mx-auto">
                {t('home.intelligence.description')}
              </p>
            </div>
            
            <div className="w-full max-w-3xl mx-auto pt-2">
              <div className="relative w-full group bg-surface-glass border border-border-glass transition-all focus-within:ring-2 focus-within:ring-primary/30 rounded-xl overflow-hidden shadow-sm">
                <form onSubmit={handleHsSearch}>
                  <input 
                    type="text" 
                    value={hsQuery}
                    onChange={(e) => setHsQuery(e.target.value)}
                    placeholder={t('home.intelligence.searchPlaceholder')} 
                    className="w-full bg-transparent border-none py-4.5 pl-12 pr-12 font-montserrat focus:ring-0 outline-none text-on-surface"
                  />
                  <Search size={20} className="absolute left-4 top-1/2 -translate-y-1/2 text-on-secondary-container" />
                  <button 
                    type="submit" 
                    disabled={loadingHs} 
                    aria-label={language === 'es' ? 'Buscar código HS' : 'Search HS Code'}
                    className="absolute right-0 top-0 bottom-0 px-6 text-primary hover:scale-105 active:scale-95 transition-transform duration-200"
                  >
                    {loadingHs ? <Loader2 className="animate-spin w-5 h-5" /> : <ArrowRight className="w-5 h-5" />}
                  </button>
                </form>
              </div>
              
              <AnimatePresence mode="wait">
                {searchError && !loadingHs && (
                  <motion.div 
                    initial={{ opacity: 0, y: 5 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0 }}
                    className="mt-4 p-4 border border-red-500/20 bg-red-400/5 rounded-xl text-center mx-auto"
                  >
                    <p className="text-xs font-bold text-red-500 tracking-wide flex items-center justify-center gap-2">
                      <span>⚠️</span> {searchError}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            {/* Quick search suggestions tags */}
            <div className="pt-2 space-y-3 w-full text-center flex flex-col items-center">
              <span className="text-[11px] font-extrabold tracking-wider text-primary uppercase block">
                {language === 'en' ? 'Quick suggestions:' : 'Sugerencias rápidas de búsqueda:'}
              </span>
              <div className="flex flex-wrap justify-center gap-2.5">
                {[
                  { label: language === 'en' ? '🥑 Avocado' : '🥑 Aguacate', code: '0804.40.01' },
                  { label: language === 'en' ? '📱 Smartphones' : '📱 Teléfonos m.', code: '8517.13.01' },
                  { label: language === 'en' ? '👟 Footwear' : '👟 Calzado corp.', code: '6403.99.99' },
                  { label: language === 'en' ? '💊 Medicines' : '💊 Medicamentos', code: '3004.90.99' },
                  { label: language === 'en' ? '🥩 Beef' : '🥩 Carne bovino', code: '0201.20.01' },
                  { label: language === 'en' ? '🚗 Autoparts' : '🚗 Autopartes', code: '8708.99.99' },
                  { label: language === 'en' ? '👕 Clothing' : '👕 Prendas vestir', code: '6109.10.01' },
                  { label: language === 'en' ? '💻 Laptops' : '💻 Laptops/PC', code: '8471.30.01' },
                  { label: language === 'en' ? '🍷 Wine' : '🍷 Vino', code: '2204.21.01' },
                  { label: language === 'en' ? '🏗️ Steel' : '🏗️ Acero', code: '7210.49.01' },
                ].map((s, si) => (
                  <button
                    key={si}
                    onClick={() => {
                      setHsQuery(s.code);
                      handleHsSearch(undefined, s.code);
                    }}
                    aria-label={`${language === 'en' ? 'Search for' : 'Buscar'} ${s.label.replace('🥑', '').replace('📱', '').replace('👟', '').replace('💊', '').replace('🥩', '').replace('🚗', '').replace('👕', '').replace('💻', '').replace('🍷', '').replace('🏗️', '').trim()}`}
                    className="text-xs px-3.5 py-2 border border-border-glass bg-primary/5 hover:bg-primary/10 hover:border-primary/40 rounded-full transition-all text-on-surface flex items-center gap-2 outline-none font-medium cursor-pointer"
                  >
                    <span>{s.label}</span>
                    <span className="opacity-40 font-mono text-[10px]">({s.code})</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div id="ai-dashboard-anchor" className="w-full mt-12">
            <AnimatePresence mode="wait">
              {loadingHs ? (
                <motion.div
                  key="loading"
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -30 }}
                  className="w-full max-w-3xl mx-auto pt-8"
                >
                  <GlassCard className="!p-8 border border-primary/50 bg-primary/5 rounded-2xl flex flex-col items-center justify-center gap-6 text-center min-h-[220px] hover:shadow-2xl hover:shadow-primary/15 transition-all duration-500 w-full">
                    <Loader2 className="w-12 h-12 text-primary animate-spin" />
                    <div className="space-y-2">
                      <p className="text-base font-bold uppercase tracking-widest text-primary">
                        {language === 'en' ? 'Querying Trade Database...' : 'Consultando Base de Datos...'}
                      </p>
                      <p className="text-xs text-on-secondary-container leading-relaxed max-w-md mx-auto">
                        {language === 'en' 
                          ? 'Analyzing Mexican tariff structures, historical entry logs, and official NOM regulations.'
                          : 'Analizando estructuras aranceles, bitácoras de ingreso y normatividad oficial de aduanas.'}
                      </p>
                    </div>
                  </GlassCard>
                </motion.div>
              ) : cloudRunResult ? (
                <motion.div 
                  key="dashboard"
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: 'auto', opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  className="overflow-hidden mt-8"
                >
                  <div className="pt-8 border-t border-border-glass mb-12">
                    <div className="flex items-center gap-4 mb-8">
                      <div className="h-px bg-border-glass flex-grow" />
                      <span className="label-caps text-primary font-bold tracking-widest uppercase text-[10px]">
                        {language === 'en' ? 'Cloud Run Tariff Ecosystem' : 'Ecosistema Arancelario de Cloud Run'}
                      </span>
                      <div className="h-px bg-border-glass flex-grow" />
                    </div>
                    <Suspense fallback={<div>Cargando dashboard...</div>}>
                      <CloudRunDashboard 
                        data={cloudRunResult} 
                        resultsList={cloudRunResultsList}
                        onSelectResult={(selected) => setCloudRunResult(selected)}
                        searchQuery={hsQuery}
                      />
                    </Suspense>
                  </div>
                </motion.div>
              ) : null}
            </AnimatePresence>
          </div>
        </div>
      </section>

      {/* Location / Headquarters Section */}
      <section className="py-24 px-6 md:px-12 border-t border-border-glass bg-surface-container-lowest/30 relative">
        <div className="max-w-7xl mx-auto grid grid-cols-1 lg:grid-cols-12 gap-16 items-center">
          <div className="lg:col-span-5 space-y-6">
            <span className="label-caps text-primary">
              {language === 'es' ? 'Presencia Física' : 'Physical Location'}
            </span>
            <h2 className="font-hanken text-4xl md:text-5xl font-bold tracking-tighter leading-tight">
              {language === 'es' ? 'Nuestras Oficinas Centrales' : 'Our Headquarters'}
            </h2>
            <p className="text-on-secondary-container text-lg leading-relaxed italic font-light">
              {language === 'es' 
                ? 'Conozca las instalaciones centrales de TREID en San Luis Potosí, el núcleo de nuestras operaciones donde ingenieros de software, expertos arancelarios y asesores aduanales colaboran para blindar el comercio exterior en México.' 
                : 'Visit TREID\'s central offices in San Luis Potosi, the core of our operations where software engineers, tariff experts, and customs advisors collaborate to secure international trade in Mexico.'
              }
            </p>
            <div className="pt-2">
              <a 
                href="https://maps.app.goo.gl/9ruCxCVbqLXYksEx7" 
                target="_blank" 
                rel="noreferrer"
                className="inline-flex items-center gap-3 text-primary hover:text-primary/85 transition-colors group text-xs font-black uppercase tracking-widest"
              >
                <span className="p-3 border border-primary/20 bg-primary/5 rounded-full group-hover:scale-110 group-hover:bg-primary/10 transition-all duration-300">
                  <MapPin size={18} className="animate-bounce" />
                </span>
                <span className="underline underline-offset-4 decoration-primary/30 group-hover:decoration-primary">
                  {language === 'es' ? 'Dirección en Google Maps' : 'In-Maps Location Route'}
                </span>
                <ArrowRight size={14} className="group-hover:translate-x-1.5 transition-transform" />
              </a>
            </div>
          </div>
          
          <div className="lg:col-span-7">
            <div className="relative group rounded-2xl overflow-hidden border border-border-glass aspect-[4/3] sm:aspect-video shadow-2xl bg-surface-container-low">
              <a 
                href="https://maps.app.goo.gl/9ruCxCVbqLXYksEx7" 
                target="_blank" 
                rel="noreferrer"
                className="absolute inset-0 block cursor-pointer z-10"
              >
                <img 
                  src="https://images.unsplash.com/photo-1524661135-423995f22d0b?auto=format&fit=crop&q=80&w=1200" 
                  alt="San Luis Potosí, México" 
                  width="1200"
                  height="675"
                  className="w-full h-full object-cover grayscale brightness-[0.7] group-hover:scale-105 group-hover:grayscale-0 group-hover:brightness-[0.8] transition-all duration-700"
                  referrerPolicy="no-referrer"
                  loading="lazy"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = 'https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?q=80&w=1200';
                  }}
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/55 to-transparent flex flex-col justify-end p-6 sm:p-8 md:p-12">
                  <span className="label-caps text-primary text-[10px] sm:text-xs mb-2 flex items-center gap-1.5">
                    <span className="w-2 h-2 sm:w-2.5 sm:h-2.5 rounded-full bg-primary animate-ping"></span>
                    <span className="font-extrabold tracking-widest">TREID SAN LUIS POTOSÍ</span>
                  </span>
                  <h3 className="font-hanken text-lg sm:text-2xl md:text-3.5xl font-extrabold text-white tracking-tighter leading-tight mb-2">
                    {language === 'es' ? 'San Luis Potosí, México (Sede Central)' : 'San Luis Potosi, Mexico (HQ)'}
                  </h3>
                  <p className="text-[11px] sm:text-xs text-white/90 font-medium max-w-lg mb-4 leading-relaxed group-hover:text-white transition-colors">
                    {language === 'es' 
                      ? 'Navega en tiempo real a nuestras oficinas para un diagnóstico comercial en persona.' 
                      : 'Navigate in real-time to our office for an in-person trade and customs audit diagnostics.'
                    }
                  </p>
                  <div className="flex items-center gap-2 text-primary font-bold text-[9px] sm:text-[10px] uppercase tracking-widest">
                    <span>{language === 'es' ? 'Navegar en Google Maps' : 'Navigate on Google Maps'}</span>
                    <ArrowRight size={12} className="group-hover:translate-x-1 transition-transform" />
                  </div>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}

